/**
 * Traduce el payload de `activity_log.details` (columna JSONB) a pares
 * etiqueta/valor legibles.
 *
 * El backend guarda claves distintas según la acción y no guarda la unidad del
 * producto, así que aquí no se añaden sufijos (g/ud). Las ubicaciones se guardan
 * como UUID crudo: se resuelven contra el mapa `locations` (id → nombre) que
 * pasa la página.
 *
 * `locations` es `null` cuando no se pudo cargar (un STAFF sin permiso de
 * productos recibe 403 de `/v1/stock/locations`). En ese caso se OMITEN las
 * ubicaciones en vez de pintar un UUID o inventarse un nombre.
 */

// Los decimales llegan como string ("250.000"). Se recortan los ceros de cola.
function fmtNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("es-ES", { maximumFractionDigits: 3 });
}

// Coste de la entrada de mercancía: es por unidad (€/g o €/ud), no un importe total.
// El backend no guarda la unidad del producto, así que no se sufija.
function fmtUnitCost(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `${n.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}/ud.`;
}

// Etiquetas de los campos del socio, para no pintarle `credit_balance` a un humano.
const CLIENT_FIELDS = {
  email: "Email",
  first_name: "Nombre",
  last_name: "Apellidos",
  national_id: "DNI",
  birth_date: "Fecha de nacimiento",
  address: "Dirección",
  postal_code: "Código postal",
  city: "Ciudad",
  phone: "Teléfono",
  credit_balance: "Saldo",
  enrollment_date: "Fecha de alta",
  expiration_date: "Fecha de caducidad",
  guarantor_member: "Avalista",
  fee: "Cuota",
  max_monthly_consumption: "Consumo máx. mensual",
  notes: "Notas",
  cost_line: "Línea de coste",
  discount: "Descuento",
  consumption_ludenos: "Consumo lúdico",
  consumption_terap: "Consumo terapéutico",
  // Campos censurados: el backend registra QUE cambiaron, nunca su valor.
  password: "Contraseña",
  documents_path: "Documentos",
  dni_documents_path: "Documentación DNI",
  dni_front_path: "DNI (anverso)",
  dni_back_path: "DNI (reverso)",
};

const clientField = (key) => CLIENT_FIELDS[key] || key;

// Campos cuyo VALOR no se pinta nunca, venga como venga del backend.
//
// El backend ya los redacta (los manda en `redacted_fields`, sin valor). Esto es la segunda
// cerradura: esta página la lee el STAFF, pinta los pares en la fila de la tabla y los mete
// en el buscador — si el backend regresara, un camarero podría teclear un DNI y encontrar al
// socio. Y el log no tiene FK al socio: lo que se escriba aquí sobrevive a su borrado.
//
// `consumption_terap` / `consumption_ludenos` son dato de SALUD (RGPD art. 9).
const NEVER_SHOW_VALUE = new Set([
  "national_id", "birth_date",
  "address", "postal_code", "city", "phone",
  "consumption_terap", "consumption_ludenos",
  "password", "documents_path", "dni_documents_path", "dni_front_path", "dni_back_path",
]);

// Un valor vacío se lee mejor como "—" que como una cadena en blanco.
function fmtChangeValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (value === true) return "Sí";
  if (value === false) return "No";
  return String(value);
}

export function formatActivityDetails(action, details, locations) {
  if (!details || typeof details !== "object" || Array.isArray(details)) return [];

  const out = [];
  const push = (label, value) => {
    if (value === null || value === undefined || value === "") return;
    out.push({ label, value: String(value) });
  };
  // Una ubicación presente en el payload pero ausente del mapa ya cargado es una
  // ubicación borrada después del movimiento — el log es histórico, la lista no.
  // `locations` es legítimamente null: el STAFF recibe 403 de /v1/stock/locations. Sin esta
  // guarda, la próxima llamada a locName sin comprobar antes deja la página en blanco justo
  // a los usuarios de menos privilegio.
  const locName = (id) => {
    if (!locations) return null;
    return locations[id] || "Ubicación eliminada";
  };
  const pushLoc = (label, id) => {
    if (!id || !locations) return;
    push(label, locName(id));
  };

  // Regla: NO repetir lo que el `summary` ya dice. El summary de una entrada ya trae la
  // cantidad y el producto; el de un ajuste, la variación; el de una venta, el importe,
  // el método de pago y el número de líneas. Repetirlo era justo lo que hacía que el
  // desplegable no aportara nada.
  switch (action) {
    case "stock.entry":
    case "stock.exit":
      // `quantity` ya está en el summary.
      pushLoc("Ubicación", details.location_id);
      // Solo la entrada de mercancía tiene coste (lo que se pagó por el género).
      push("Coste", fmtUnitCost(details.unit_cost));
      push("Saldo", fmtNumber(details.balance_after));
      push("Concepto", details.concept);
      break;

    case "stock.adjust":
      // `new_quantity` y `delta` ya están en el summary.
      pushLoc("Ubicación", details.location_id);
      push("Saldo", fmtNumber(details.balance_after));
      push("Concepto", details.concept);
      break;

    case "stock.set_total":
      // `new_total` y `delta` ya están en el summary: solo el concepto es nuevo.
      push("Concepto", details.concept);
      break;

    case "stock.transfer":
      // `quantity` ya está en el summary; lo que falta es de dónde a dónde.
      if (locations && (details.from_location_id || details.to_location_id)) {
        const from = details.from_location_id ? locName(details.from_location_id) : "—";
        const to = details.to_location_id ? locName(details.to_location_id) : "—";
        push("Movimiento", `${from} → ${to}`);
      }
      break;

    case "sale.create":
      // Nada que añadir: importe, pago, carrito y nº de líneas ya están TODOS en el
      // summary, y `member_user_id` es un UUID sin nombre que resolver. Lo que de verdad
      // se quiere saber de una venta —qué productos, a qué socio, a qué precio— no está
      // en el log: vive en la transacción, y a eso baja el drill-down del modal.
      break;

    case "socio.update": {
      // Qué cambió, campo a campo: "Saldo: 120 € → 95 €".
      const changes = details.changes || {};
      const censurados = [];
      for (const [field, change] of Object.entries(changes)) {
        if (!change || typeof change !== "object") continue;
        // Si el backend mandara el valor de un campo sensible (regresión), NO se pinta:
        // se degrada al mismo trato que los redactados.
        if (NEVER_SHOW_VALUE.has(field)) {
          censurados.push(field);
          continue;
        }
        push(clientField(field), `${fmtChangeValue(change.from)} → ${fmtChangeValue(change.to)}`);
      }
      // Campos censurados por el backend (identidad, contacto, salud, contraseña): consta
      // que se tocaron, nunca su contenido. No hay valor que pintar, solo el hecho.
      const redacted = [...new Set([...(details.redacted_fields || []), ...censurados])];
      if (redacted.length) {
        push("También se actualizó", redacted.map(clientField).join(", "));
      }
      break;
    }

    case "socio.create":
    case "staff.create":
    case "owner.create":
      push("Email", details.email);
      break;

    default: {
      // Acción nueva en el backend y todavía sin receta aquí: se listan las claves planas
      // tal cual, para degradar a algo legible en vez de volver al JSON.
      //
      // Pero a ciegas no: cualquier acción futura que registre un campo sensible acabaría
      // pintándolo aquí sin que nadie lo revisara. Se listan por nombre, sin valor.
      const censurados = [];
      for (const [key, value] of Object.entries(details)) {
        if (value === null || value === undefined || value === "") continue;
        if (typeof value === "object") continue;
        if (NEVER_SHOW_VALUE.has(key)) {
          censurados.push(key);
          continue;
        }
        push(key, value);
      }
      if (censurados.length) {
        push("También se registró", censurados.map(clientField).join(", "));
      }
      break;
    }
  }

  return out;
}

export default formatActivityDetails;
